import React, { useState } from 'react';
import {
  User,
  LogOut,
  Settings,
  ChevronDown,
  Menu,
  X,
  Mail,
  UserCircle,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import ProfileModal from '../common/ProfileModal';
import HelpButton from '../common/HelpButton';
import NotificationBell from '../layout/NotificationBell';
import './Header.css';
import { useAI } from '../../context/AIContext';

const Header = ({ onToggleSidebar, sidebarOpen }) => {
  const { user, logout, getCompanyName, getCompanyId } = useAuth();
  const { aiEnabled, toggleAI } = useAI();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
      // The AuthContext will handle redirecting to login
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const getUserInitials = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName.charAt(0)}${user.lastName.charAt(
        0,
      )}`.toUpperCase();
    }
    if (user?.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return 'U';
  };

  const getUserDisplayName = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user?.email) {
      return user.email;
    }
    return 'User';
  };

  return (
    <header
      className="header-container"
      style={{
        height: '50px',
        backgroundColor: '#FFFFFF',
        borderBottom: '1px solid rgba(45, 125, 210, 0.2)',
        paddingLeft: '0',
        paddingRight: '24px',
      }}
    >
      {/* Left Section - Company Name */}
      <div className="header-left" style={{ paddingLeft: '16px' }}>
        {/* Company Name Display */}
        {getCompanyName() && (
          <div
            style={{
              fontSize: '16px',
              fontWeight: '500',
              color: '#2D7DD2',
              fontFamily: 'var(--font-family)',
              padding: '8px 0',
            }}
          >
            {getCompanyName()}
          </div>
        )}
      </div>

      {/* Right Section - Notifications & User Menu */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {/* AI Toggle */}
        {/* <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label
            style={{
              fontSize: '13px',
              color: 'var(--primary-color)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <input
              type="checkbox"
              checked={!!aiEnabled}
              onChange={toggleAI}
              style={{
                width: '18px',
                height: '18px',
                color: '#3a8dcc',
              }}
            />
            <span style={{ fontSize: '13px' }}>Enable AI</span>
          </label>
        </div> */}
        
        <NotificationBell />

        {/* Help Button */}
        <HelpButton
          type="global"
          variant="icon"
          size="medium"
          className="help-btn-nav"
          openInNewWindow={true}
        />

        {/* User Menu */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '40px',
              height: '40px',
              border: '1px solid #E5E7EB',
              borderRadius: '6px',
              backgroundColor: '#FFFFFF',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              padding: 0,
            }}
            onMouseEnter={(e) => {
              e.target.style.borderColor = '#2D7DD2';
              e.target.style.backgroundColor = 'rgba(45, 125, 210, 0.05)';
            }}
            onMouseLeave={(e) => {
              e.target.style.borderColor = '#E5E7EB';
              e.target.style.backgroundColor = '#FFFFFF';
            }}
          >
            {/* User Icon */}
            <User size={18} strokeWidth={2} style={{ color: '#2D7DD2' }} />
          </button>

          {/* User Menu Dropdown */}
          {showUserMenu && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: '8px',
                width: '280px',
                backgroundColor: '#FFFFFF',
                border: '1px solid rgba(0,0,0,0.08)',
                borderRadius: '12px',
                boxShadow:
                  '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                zIndex: 1000,
                overflow: 'hidden',
              }}
            >
              {/* Header with User Info */}
              <div
                style={{
                  padding: '20px',
                  borderBottom: '1px solid rgba(0,0,0,0.08)',
                  position: 'relative',
                }}
              >
                {/* Close Button */}
                <button
                  onClick={() => setShowUserMenu(false)}
                  style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: 'none',
                    backgroundColor: 'transparent',
                    cursor: 'pointer',
                    borderRadius: '4px',
                    color: '#6B7280',
                    transition: 'all 0.2s ease',
                    padding: 0,
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#f3f4f6';
                    e.target.style.color = '#1F2937';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = '#6B7280';
                  }}
                >
                  <X size={16} />
                </button>

                {/* User Icon and Name */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '16px',
                    marginBottom: '16px',
                    paddingRight: '32px',
                  }}
                >
                  <div
                    style={{
                      width: '56px',
                      height: '56px',
                      borderRadius: '50%',
                      backgroundColor: '#2D7DD2',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <UserCircle size={32} style={{ color: '#FFFFFF' }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0, paddingTop: '2px' }}>
                    <div
                      style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        color: '#1F2937',
                        marginBottom: '4px',
                        fontFamily: 'var(--font-family)',
                        lineHeight: '1.4',
                      }}
                    >
                      {getUserDisplayName()}
                    </div>
                    {/* User Role */}
                    {user?.role?.displayName && (
                      <div
                        style={{
                          fontSize: '13px',
                          color: '#6B7280',
                          fontFamily: 'var(--font-family)',
                          lineHeight: '1.4',
                          fontWeight: '400',
                        }}
                      >
                        {user.role.displayName}
                      </div>
                    )}
                  </div>
                </div>

                {/* User Email - Left aligned */}
                {user?.email && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      paddingRight: '32px',
                      marginBottom: '12px',
                    }}
                  >
                    <Mail
                      size={14}
                      style={{ flexShrink: 0, color: '#6B7280' }}
                    />
                    <span
                      style={{
                        fontSize: '13px',
                        color: '#6B7280',
                        fontFamily: 'var(--font-family)',
                        lineHeight: '1.4',
                        wordBreak: 'break-all',
                        flex: 1,
                      }}
                    >
                      {user.email}
                    </span>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div style={{ padding: '8px 0' }}>
                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    setShowProfileModal(true);
                  }}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 20px',
                    border: 'none',
                    backgroundColor: 'transparent',
                    color: '#1F2937',
                    cursor: 'pointer',
                    fontSize: '14px',
                    transition: 'background-color 0.2s ease',
                    fontFamily: 'var(--font-family)',
                    textAlign: 'left',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#F6FAFF';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                  }}
                >
                  <User size={16} style={{ color: '#2D7DD2' }} />
                  Account
                </button>

                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    handleLogout();
                  }}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 20px',
                    border: 'none',
                    backgroundColor: 'transparent',
                    color: '#dc2626',
                    cursor: 'pointer',
                    fontSize: '14px',
                    transition: 'background-color 0.2s ease',
                    fontFamily: 'var(--font-family)',
                    textAlign: 'left',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#fef2f2';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                  }}
                >
                  <LogOut size={16} />
                  Sign out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Click outside to close dropdowns */}
      {(showUserMenu || showNotifications) && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999,
          }}
          onClick={() => {
            setShowUserMenu(false);
            setShowNotifications(false);
          }}
        />
      )}

      {/* Profile Modal */}
      <ProfileModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        user={user}
      />
    </header>
  );
};

export default Header;
